import React from 'react';
import {
  Box,
  Typography,
  Container,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from '@mui/material';
import {
  CheckCircle,
  ContactMail,
  AccessTime,
  Handshake,
  ThumbUpAlt,
  TrendingUp,
  PictureAsPdf
} from '@mui/icons-material';
import { Link } from 'react-scroll';
import newsletterPDF from "../assets/Karen_Brown_NYWBA_Newsletter_Page_12.pdf";

const benefitsList = [
  {
    icon: <AccessTime color="secondary" />, 
    text: 'Time savings. Less time spent on emotional triage means more time for high-value legal strategy'
  },
  {
    icon: <TrendingUp color="secondary" />, 
    text: 'Billable efficiency. Clearer client communication and preparation lead to more productive meetings'
  },
  {
    icon: <Handshake color="secondary" />, 
    text: 'Professional collaboration. Engagement and communication remain transparent and within ethical boundaries that respect the attorney’s role'
  },
  {
    icon: <ThumbUpAlt color="secondary" />, 
    text: 'Client satisfaction. Supported clients make more grounded decisions and are more likely to speak highly of their legal team'
  },
];

const LawFirmsSection = () => {
  return (
    <Box
      id="for law firms"
      sx={{
        py: { xs: 8, sm: 10, md: 15 },
        px: 3,
        background: 'linear-gradient(80deg, rgba(240, 255, 250, 0.85) 0%, rgba(208, 220, 216, 0.85) 100%)',
      }}
    >
      <Container maxWidth="md">
        <Typography variant="h4" gutterBottom>
          For Attorneys and Law Firms
        </Typography>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 500 }}>
          Support That Stabilizes Clients and Protects Attorney Time
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          I work within the professional divorce ecosystem alongside attorneys, mediators, financial professionals, and therapists to help clients
          remain steady, organized, and prepared throughout the legal process. When clients are emotionally overloaded, disorganized, or unclear on
          next steps, it creates unnecessary friction—impacting timelines and attorney-client communications.
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          My role is not to replace the attorney’s work, but to help clients arrive organized, emotionally steady, and prepared to engage productively
          in the legal process. As a CDC Certified Divorce Coach® & Advisor, I collaborate with attorneys by helping their clients:
        </Typography>

        <List>
          {[
            'Organize financial and account documentation so key information is easier to locate and discuss',
            'Prepare lifestyle, parenting, and income information that attorneys may need when building their case',
            'Arrive ready for legal meetings, mediation sessions, and court appearances with clearer questions and priorities',
            'Stay focused on next steps instead of becoming stalled by uncertainty or emotional overwhelm',
            'Think through important communications before reacting, helping prevent unnecessary escalation'
          ].map((item, index) => (
            <ListItem key={index} sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <CheckCircle color="secondary" />
              </ListItemIcon>
              <ListItemText primary={item} />
            </ListItem>
          ))}
        </List>

        <Typography variant="body1" sx={{ mt: 3, mb: 2 }}>
          Think of me as a client stabilization partner—someone who helps your clients show up steady, informed, and prepared so you can focus on the legal work that matters most.
        </Typography>

        <Typography variant="body1" sx={{ mb: 2 }}>
          What this means for your firm:
        </Typography>

        <List>
          {benefitsList.map((item, index) => (
            <ListItem key={index} sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItem>
          ))}
        </List>

        {/* New paragraph will go above this link */}
        <Typography variant="body1" sx={{ mt: 3, mb: 2 }}>
          This kind of collaboration benefits both clients and the attorneys guiding them through the process. I recently co-authored an article for the New York Women’s Bar Association’s October 2025 Newsletter exploring how divorce can contribute to attorney burnout and how collaborative client support can help address it:
        </Typography>

        <Typography variant="body2" sx={{ mb: 3 }}>
          <a
            href={newsletterPDF}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: "flex", alignItems: "center", gap: "6px", color: "#6C7F74", textDecoration: "underline" }}
          >
            <PictureAsPdf fontSize="small" />
            NYWBA Newsletter Feature - October 2025 (Page 12)
          </a>
        </Typography>

        <Typography
          variant="body2"
          sx={{
            mt: 4,
            fontSize: '0.775rem',
            color: 'text.secondary',
            fontStyle: 'italic',
          }}
        >
          Note: While I offer emotional and practical support, I do not provide therapy or legal advice. My
          role is non-clinical and action-oriented, focused on logistics, preparation, and strategic guidance to
          complement—not replace—your work or that of a therapist.
        </Typography>

        <Link to="contact" smooth={true} duration={500}>
          <Button variant="contained" color="secondary" size="large" sx={{ mt: 4 }} startIcon={<ContactMail />}>
            Connect About Your Clients
          </Button>
        </Link>
      </Container>
    </Box>
  );
};

export default LawFirmsSection;